import { Link } from "react-router-dom";
import { allTopics, formatHeat, formatShortDate, getCategoryLabel, sortByHeat } from "@/lib/site";
import { siteStats } from "@/mocks/siteStats";
import StatusTag from "@/components/feature/StatusTag";

export default function HeroSection() {
  const featured = sortByHeat(allTopics)[0];
  const stats = siteStats.slice(0, 3);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20editorial%20collage%20of%20news%20cards%2C%20timeline%20lines%20and%20radar%20waves%20on%20warm%20cream%20paper%20texture%20with%20coral%20and%20amber%20accents%2C%20minimal%20flat%20design%2C%20deep%20dark%20shadows%2C%20moody%20high%20contrast&width=1600&height=1000&seq=gt-hero-01&orientation=landscape"
          alt="瓜田雷达事件时间线抽象背景插图"
          title="网曝吃瓜进展追踪 - 瓜田雷达"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/72 via-black/58 to-black/78" />
      </div>

      <div className="relative w-full px-4 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
        <div className="grid w-full gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="animate-fade-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-radar-line text-primary-300" aria-hidden="true" />
              追踪中话题 68 个 · 今日新增节点 12 条
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-[44px] md:leading-[1.2]">
              网曝吃瓜进展与后续追踪
              <span className="mt-3 block text-xl font-bold text-primary-200 md:text-[26px]">
                从爆发到回应，一条时间线看清全过程
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
              把散落在各个平台的爆料、回应与澄清按发生顺序重新排列，补上背景和出处。你不需要在热搜之间来回跳转，只要顺着一条线往下读，就能知道事情发酵到哪一步、还差什么结论。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/topics"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-archive-2-line" aria-hidden="true" />
                浏览话题档案
              </Link>
              <Link
                to="/#latest"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-history-line" aria-hidden="true" />
                看最新进展
              </Link>
            </div>

            <dl className="mt-9 flex flex-wrap gap-x-8 gap-y-4">
              {stats.map((item) => (
                <div key={item.label} className="min-w-[110px]">
                  <dd className="font-heading text-2xl font-bold text-background-50">
                    {item.value}
                  </dd>
                  <dt className="mt-1 text-xs text-background-100/75">{item.label}</dt>
                  <p className="mt-1 text-[11px] text-background-100/55">{item.note}</p>
                </div>
              ))}
            </dl>
          </div>

          <div className="animate-fade-rise lg:justify-self-end lg:w-full lg:max-w-[460px]">
            <article className="overflow-hidden rounded-lg bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-5 py-3">
                <span className="flex items-center gap-2 text-xs font-label font-medium uppercase tracking-[0.18em] text-foreground-500">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                  今日热度最高
                </span>
                <span className="text-xs text-foreground-500">
                  {formatShortDate(featured.updatedAt)} 更新
                </span>
              </div>
              <Link to={`/topic/${featured.id}`} className="block h-44 w-full overflow-hidden">
                <img
                  src={featured.cover}
                  alt={`${featured.title} 话题配图`}
                  title={`${featured.title} - 进展追踪`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </Link>
              <div className="p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-secondary-200 bg-secondary-50 px-2 py-[2px] text-[11px] font-medium text-secondary-800">
                    {getCategoryLabel(featured.categoryId)}
                  </span>
                  <StatusTag label={featured.status} tone={featured.statusTone} />
                </div>
                <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                  <Link to={`/topic/${featured.id}`} className="hover:text-primary-700">
                    {featured.title}
                  </Link>
                </h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                  {featured.summary}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-node-tree" aria-hidden="true" />
                    {featured.timeline.length} 个进展节点
                  </span>
                  <span className="inline-flex items-center gap-1 font-medium text-primary-600">
                    <i className="ri-fire-line" aria-hidden="true" />
                    热度 {formatHeat(featured.heat)}
                  </span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}